import { ComponentFactoryResolver, ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { FormArray } from '@angular/forms';
import { IApplication } from '@app/core/model/applications/applications';
import { ApplicationFeatures } from '@app/core/model/applications/application-features';
import { ValidationUpdate } from '../bs-app-launch-form.component';
import { BsAppLaunchCheckboxControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-checkbox-control/bs-app-launch-checkbox-control.component';
import { CheckBoxFormControl } from '../bs-app-launch-form-controls/bs-app-launch-checkbox-control/checkbox.control';
import { BsAppLaunchFieldSetControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-field-set-control/bs-app-launch-field-set-control.component';
import { FieldSetFormControl } from '../bs-app-launch-form-controls/bs-app-launch-field-set-control/field-set.control';
import { BsAppLaunchResourceChooserControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-resource-chooser-control/bs-app-launch-resource-chooser-control.component';
import { ResourceChooserFormControl } from '../bs-app-launch-form-controls/bs-app-launch-resource-chooser-control/resource-chooser.control';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import _ from 'lodash';
import { IUser } from '@app/core/model/user';
import { BsAppLaunchTextAreaControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-text-area-control/bs-app-launch-text-area-control.component';
import { TextAreaFormControl } from '../bs-app-launch-form-controls/bs-app-launch-text-area-control/text-area.control';
import { BsAppLaunchTextInputControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-text-input-control/bs-app-launch-text-input-control.component';
import { TextBoxFormControl } from '../bs-app-launch-form-controls/bs-app-launch-text-input-control/text-box.control';
import { BasespaceFormArray, BasespaceFormControl, AttributeType, FormControlType, UpdateType, getTogglerValues } from '../bs-app-launch-form-controls/bs-app-launch-form-control';
import { SelectFormControl } from '../bs-app-launch-form-controls/bs-app-launch-select-control/select.control';
import { BsAppLaunchSelectControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-select-control/bs-app-launch-select-control.component';
import { BsAppLaunchTabularFieldSetControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-tabular-field-set-control/bs-app-launch-tabular-field-set-control.component';
import { TabularFieldSetFormControl } from '../bs-app-launch-form-controls/bs-app-launch-tabular-field-set-control/tabular-field-set.control';
import { BsAppLaunchNumericControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-numeric-control/bs-app-launch-numeric-control.component';
import { NumericFormControl } from '../bs-app-launch-form-controls/bs-app-launch-numeric-control/numeric.control';
import { BsAppLaunchRadioButtonControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-radio-button-control/bs-app-launch-radio-button-control.component';
import { RadioButtonFormControl } from '../bs-app-launch-form-controls/bs-app-launch-radio-button-control/radio-button.control';
import { BsAppLaunchSectionBreakControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-section-break-control/bs-app-launch-section-break-control.component';
import { SectionBreakFormControl } from '../bs-app-launch-form-controls/bs-app-launch-section-break-control/section-break.control';
import { BsAppLaunchResourceMatcherControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-resource-matcher-control/bs-app-launch-resource-matcher-control.component';
import { ResourceMatcherFormControl } from '../bs-app-launch-form-controls/bs-app-launch-resource-matcher-control/resource-matcher.control';
import { isNullOrUndefined } from 'util';
import { Subject } from 'rxjs';
import { StringUtilities } from '@app/core/utilities/string-utilities';
import { FormValidationResponse } from '../bs-app-launch-form.component';

@Injectable({
  providedIn: 'root'
})
export class BsAppLaunchFormService {
  localDateTime: string;
  bioSampleRegistryEnabled: boolean;
  currentApp: IApplication;
  currentUser: IUser;
  formArray: FormArray;
  validationResponse: FormValidationResponse;
  
  // Subject to emit when a control's value has changed and should trigger form updates
  public formUpdateTrigger$ = new Subject<{ controlId: string }>();

  // BASE-88989: per-app-version feature flag. When enabled, enabling/disabling a field
  // re-evaluates that field's own togglers so toggle state cascades. When not enabled,
  // toggling stays non-cascading (emitEvent: false) as introduced in BASE-88847.
  get togglerCascadingEnabled(): boolean {
    return !!(this.currentApp && this.currentApp.Features
      && this.currentApp.Features.includes(ApplicationFeatures.ENABLE_TOGGLER_CASCADING));
  }
  

  constructor(
    private resolver: ComponentFactoryResolver,
  ) { }

  initializeForm(fields: any[], formContainer: ViewContainerRef, formArray: FormArray): void {
    fields.forEach((field) => {
      this.initializeFormField(field, formContainer, formArray);
    });
  }

  initializeFormField(rawField: any, formContainer: ViewContainerRef, formArray: FormArray, basespaceFormControl?: BasespaceFormControl<any> | BasespaceFormArray<any>): void {
    let componentType;
    let formControl = basespaceFormControl ? basespaceFormControl : null;
    const formControlParameterSupplied = !!formControl;
    const field = StringUtilities.deepCamelCase(rawField);

    // App session name is a special field as it's used as the 'Name' property for the app session and shows up in analyses master list.
    // This behavior of replacing the existing value with '<app-name> <date>' was carried over from old app launch UI.
    if(field.id && field.id.toLowerCase() === 'app-session-name') {
      field.value = `${this.currentApp.Name} [LocalDateTime]`;
    }
    
    switch (field.formControlType) {
      case FormControlType.TEXT_BOX:
          componentType = BsAppLaunchTextInputControlComponent;

          if(!formControl) {
            field.value = field.value ? field.value.replace("[LocalDateTime]", this.localDateTime) : field.value;
            formControl = new TextBoxFormControl({
              ...field,
            });
          }
          break;
      case FormControlType.FIELD_SET:
        componentType = BsAppLaunchFieldSetControlComponent;
        if(!formControl) {
          formControl = new FieldSetFormControl({
            ...field
          });
        }
        break;
      case FormControlType.TABULAR_FIELD_SET:
          componentType = BsAppLaunchTabularFieldSetControlComponent;
          if(!formControl) {
            formControl = new TabularFieldSetFormControl({
              ...field
            });
          }
          break;
        case FormControlType.RUN_CHOOSER:
        case FormControlType.PROJECT_CHOOSER:
        case FormControlType.V1_DATASET_FILE_CHOOSER:
        case FormControlType.FILE_CHOOSER:
        case FormControlType.V1_DATASET_CHOOSER:
        case FormControlType.APP_RESULT_CHOOSER:
        case FormControlType.SAMPLE_CHOOSER:
        case FormControlType.V1_BIOSAMPLE_CHOOSER:
          componentType = BsAppLaunchResourceChooserControlComponent;
          if(!formControl) {
            field.resourceType = this.getChooserResourceType(field.formControlType);
            formControl = new ResourceChooserFormControl({
              ...field
            });
          }
        break;
      case FormControlType.TEXT_AREA:
          componentType = BsAppLaunchTextAreaControlComponent;
          if(!formControl) {
            formControl = new TextAreaFormControl({
              ...field,
            });
           }
        break;
      case FormControlType.CHECK_BOX:
          componentType = BsAppLaunchCheckboxControlComponent;
          if(!formControl) {
            formControl = new CheckBoxFormControl({
              ...field
            });
          }
          break;
      case FormControlType.SELECT:
          componentType = BsAppLaunchSelectControlComponent;
          if(!formControl) { 
            formControl = new SelectFormControl({
              ...field
            });
          }
            break;
        case FormControlType.NUMERIC:
            componentType = BsAppLaunchNumericControlComponent;
            if(!formControl) {
              formControl = new NumericFormControl({
                ...field
              });
            }
            break;
        case FormControlType.RADIO_BUTTON:
            componentType = BsAppLaunchRadioButtonControlComponent;
            if(!formControl) {
              formControl = new RadioButtonFormControl({
                ...field
              });
            }
            break;
        case FormControlType.SECTION_BREAK:
            componentType = BsAppLaunchSectionBreakControlComponent;
            if(!formControl) { 
              formControl = new SectionBreakFormControl({
                ...field
              });
            }
            break;
        case "ResourceMatcher":
        case "V1V2ResourceMatcher":
            field.resourceType = this.getMatcherResourceType(field.formControlType, field.columns[0].resourceType);
            componentType = BsAppLaunchResourceMatcherControlComponent;
            if(!formControl) { 
              formControl = new ResourceMatcherFormControl({
                ...field
              });
            }
            break;
        default:
            console.log(`"${field.formControlType}" component type not implemented yet`);
            return;
      }

    const componentFactory = this.resolver.resolveComponentFactory(componentType);

    // Docs: GenerateFormFromAppSession within src/Cloud.Domain/Logic/FormPropertiesLogic.cs
    const componentRef = formContainer.createComponent(componentFactory) as ComponentRef<any>;
    formControl.componentReference = componentRef;
    if(!formControlParameterSupplied) {
      formArray.push(formControl);  
    }
    componentRef.instance.formControl = formControl;
  }

  getMatcherResourceType(componentName: string, columnResourceType: 'AppResult' | 'Sample'): SearchResourceType {
    switch(componentName) {
      case 'ResourceMatcher':
        return columnResourceType === 'AppResult' ? SearchResourceType.AppResult : SearchResourceType.Sample;
      case "V1V2ResourceMatcher":
        return columnResourceType === 'AppResult' ? SearchResourceType.Dataset : SearchResourceType.BiosampleLibraryPrep;
      default:
        return null;
    }
  }

  getChooserResourceType(componentName: string): SearchResourceType {
    switch(componentName) {
      case FormControlType.RUN_CHOOSER:
        return SearchResourceType.Run;
      case FormControlType.PROJECT_CHOOSER:
        return SearchResourceType.Project;
      case FormControlType.V1_DATASET_FILE_CHOOSER:
        return SearchResourceType.File;
      case FormControlType.FILE_CHOOSER:
        return SearchResourceType.AppResultFile;
      case FormControlType.V1_DATASET_CHOOSER:
        return SearchResourceType.Dataset;
      case FormControlType.APP_RESULT_CHOOSER:
        return SearchResourceType.AppResult;
      case FormControlType.SAMPLE_CHOOSER:
        return SearchResourceType.Sample;
      case FormControlType.V1_BIOSAMPLE_CHOOSER:
        return SearchResourceType.Biosample;
      default:
        return null;
    }

  }

  flattenLaunchForm(formArray: FormArray): BasespaceFormControl<any>[] {
      return formArray.controls.reduce((flattenedArray, current) => {
          return flattenedArray.concat((current instanceof BasespaceFormArray || current instanceof FormArray) ? this.flattenLaunchForm(current) : current);
      }, []);
  }

// Recursively searches for all controls of the specified type at any depth
getControlsByType<T extends BasespaceFormControl<any> | BasespaceFormArray<any>>(
  formArray: FormArray, 
  controlType: new (...args: any[]) => T
): T[] {
  const results: T[] = [];
  
  formArray.controls.forEach(control => {
    // Check if the current control is of the target type
    if (control instanceof controlType) {
      results.push(control as T);
    }
    
    // If the control is a FormArray or BasespaceFormArray, recursively search its children
    if (control instanceof BasespaceFormArray || control instanceof FormArray) {
      results.push(...this.getControlsByType(control, controlType));
    }
  });
  
  return results;
}

  getFormControlById(formControlId: string, formArray: FormArray): BasespaceFormControl<any> | BasespaceFormArray<any> {
      for (let i = 0; i < formArray.controls.length; i++) {
          const control = formArray.controls[i];
          const typedControl = control as BasespaceFormControl<any> | BasespaceFormArray<any>;
          
          // Check if the current control matches the ID
          if (typedControl.id && formControlId && typedControl.id.toLowerCase() === formControlId.toLowerCase()) {
              return typedControl;
          }
          
          // If the control is a FormArray or BasespaceFormArray, recursively search its children
          if (control instanceof BasespaceFormArray || control instanceof FormArray) {
              const found = this.getFormControlById(formControlId, control);
              if (found) {
                  return found;
              }
          }
      }
      return null;
  }

  removeValidationEntriesForRow(tableId: string, deletedRowIndex: number): void {
    if (!this.validationResponse) {
      return;
    }

    const deletedRowNumber = deletedRowIndex + 1;
    const tablePrefix = `${tableId}.`;

    this.validationResponse.Errors =
      BsAppLaunchFormService.filterAndReindexValidationEntries(this.validationResponse.Errors, tablePrefix, deletedRowNumber);
    this.validationResponse.Warnings =
      BsAppLaunchFormService.filterAndReindexValidationEntries(this.validationResponse.Warnings, tablePrefix, deletedRowNumber);
    this.validationResponse.HasErrors = this.validationResponse.Errors && this.validationResponse.Errors.length > 0;
    this.validationResponse.HasWarnings = this.validationResponse.Warnings && this.validationResponse.Warnings.length > 0;

    this.clearControlValidationStatus();
    this.applyValidationResponse(this.validationResponse);
  }

  resetBasespaceValidationStatus(): void {
    this.validationResponse = null;
    this.clearControlValidationStatus();
  }

  clearControlValidationStatus(): void {
    if (!this.formArray) {
      return;
    }
    this.flattenLaunchForm(this.formArray).forEach((control: BasespaceFormControl<any>) => {
      control.resetBasespaceValidationStatus();
    });
  }

  applyValidationResponse(validationResponse: FormValidationResponse): void {
    this.validationResponse = validationResponse;

    if (validationResponse.HasErrors) {
      validationResponse.Errors.forEach((error) => {
        const formControl = this.getFormControlById(error.ControlId, this.formArray);
        if (formControl && formControl instanceof BasespaceFormControl) {
          formControl.updateControl({
            updateType: UpdateType.ADD,
            attributeType: AttributeType.ERROR,
            attributeValue: { message: error.Message }
          });
        }
      });
    }

    if (validationResponse.HasWarnings) {
      validationResponse.Warnings.forEach((warning) => {
        const formControl = this.getFormControlById(warning.ControlId, this.formArray);
        if (formControl && formControl instanceof BasespaceFormControl) {
          formControl.updateControl({
            updateType: UpdateType.ADD,
            attributeType: AttributeType.WARNING,
            attributeValue: { message: warning.Message }
          });
        }
      });
    }
  }

  /**
   * Removes validation entries whose ControlId matches the deleted row, then
   * decrements row numbers for all entries after the deleted row so indices
   * stay in sync with the form tree.
   */
  static filterAndReindexValidationEntries(
    entries: ValidationUpdate[],
    tablePrefix: string,
    deletedRowNumber: number
  ): ValidationUpdate[] {
    if (!entries) {
      return entries;
    }

    const rowSuffix = `.row${deletedRowNumber}`;

    return entries
      .filter((entry) => {
        return !(entry.ControlId && entry.ControlId.startsWith(tablePrefix) && entry.ControlId.endsWith(rowSuffix));
      })
      .map((entry) => {
        if (!entry.ControlId || !entry.ControlId.startsWith(tablePrefix)) {
          return entry;
        }

        const rowMatch = entry.ControlId.match(/\.row(\d+)$/);
        if (!rowMatch) {
          return entry;
        }

        const entryRowNumber = parseInt(rowMatch[1], 10);
        if (entryRowNumber > deletedRowNumber) {
          return { ...entry, ControlId: entry.ControlId.replace(/\.row\d+$/, `.row${entryRowNumber - 1}`) };
        }

        return entry;
      });
  }

  handleAllTogglers(): void {
    const allControls = this.flattenLaunchForm(this.formArray);
    allControls
      .filter((control) => control && control['togglers'] && control['togglers'].length > 0)
      .forEach((control) => {
        const values = getTogglerValues(control, this.togglerCascadingEnabled);
        control['togglers'].forEach((toggler: any) => {
          this.toggle(toggler.toggleOnValue, toggler.toggleFields, values);
        });
      });
  }

  // Documentation: https://bespin-developer.cloud-test.illumina.com/docs/content/documentation/apptools/formbuilder-overview#Togglers
  // Reference: libs/basespace/src/Cloud.Web/Scripts/app/appSessionStart.js
  //   - toggleOnValue trim: handleToggled() line 668 — value.trim() before $.inArray comparison
  //   - toggleFields trim: toggleFields() line 635 — fields[i].trim() before DOM lookup
  //   - togglerValue trim: NOT in legacy code, added here to guard against whitespace in form JSON choice values
  toggle(toggleOnValue: number | string, toggleOnField: string | number, togglerValues: (string | number)[]): void {
    let enableToggle

    // Empty toggleOnValue (null, undefined, or "") is a special case meaning
    // "enable the toggle field when the toggler control has no selected values."
    // The old A1 form (appSessionStart.js getTogglerOptions) converts empty string to []
    // then handleToggled enables when both toggleOnValues and togglerVals are empty.
    if(isNullOrUndefined(toggleOnValue) || toggleOnValue === '') {
      enableToggle = togglerValues.length === 0;
    } else {
      const toggleValues = String(toggleOnValue).split(',');
      enableToggle = toggleValues.some((enableToggleValue) => {
        return togglerValues.some((togglerValue) => String(togglerValue).trim() === enableToggleValue.trim())
      });
    }

    // BASE-88989: when cascading is enabled, emit valueChanges on enable/disable so a target
    // field's own toggler re-fires (via its component's valueChanges subscription), letting
    // toggle state cascade. When the flag is off we suppress events (the BASE-88847 behavior).
    const emitEvent = this.togglerCascadingEnabled;

    const toggleFields = String(toggleOnField).split(',');
    toggleFields.forEach((fieldId) => {
      const formControl = this.getFormControlById(fieldId.trim(), this.formArray);
      if(formControl) {
          if(enableToggle) {
            formControl.enable({ emitEvent });
          } else {
            formControl.disable({ emitEvent });
          }
      } else {
        console.log(`Could not find form control with id '${fieldId}'`);
      }
    });

  }
}
